import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../config/supabaseClient';

const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for error parameters in the URL hash FIRST, before Supabase processes anything
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        // If there are error parameters, check if user is still verified
        // Sometimes Supabase returns errors for redirect URL mismatches even when token is valid
        if (error || errorCode) {
          console.warn('Auth callback error detected:', { error, errorCode, errorDescription });
          
          // Check if user is actually authenticated despite the error
          // This can happen if redirect URL doesn't match Supabase config but token is valid
          const { data: { session: errorSession } } = await supabase.auth.getSession();
          
          if (errorSession?.user) {
            // User is verified! The error was likely just a redirect URL mismatch
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            navigate('/', { replace: true });
            return;
          }
          
          // User is not verified, handle the error
          console.error('Auth callback error - user not verified:', { error, errorCode, errorDescription });
          
          // Clear the hash to prevent Supabase from processing it
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          
          // Handle specific error cases
          if (errorCode === 'otp_expired' || error === 'access_denied') {
            // Check if it's actually expired or just a redirect URL issue
            const errorMessage = errorDescription 
              ? encodeURIComponent(errorDescription.replace(/\+/g, ' '))
              : 'email_link_expired';
            navigate(`/login?error=${errorMessage}`, { replace: true });
            return;
          }
          
          // Generic error handling
          navigate('/login?error=verification_failed', { replace: true });
          return;
        }

        // Only proceed if there are no errors in the hash
        // Check if there's an access_token in the hash (valid token)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        // If we have access_token and refresh_token, set the session explicitly
        if (accessToken && refreshToken && type === 'signup') {
          const { data: sessionData, error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (setSessionError) {
            console.error('Error setting session from tokens:', setSessionError);
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            navigate('/login?error=verification_failed', { replace: true });
            return;
          }
          
          if (sessionData?.session) {
            // Email verified successfully, clear hash and redirect to home page
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
            navigate('/', { replace: true });
            return;
          }
        }
        
        // Try to get session - Supabase will automatically handle the token from the URL hash
        // This handles cases where tokens are processed automatically
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Auth callback session error:', sessionError);
          // Clear hash on error
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          navigate('/login?error=verification_failed', { replace: true });
          return;
        }

        if (session) {
          // Email verified successfully, clear hash and redirect to home page
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          navigate('/', { replace: true });
        } else {
          // No session, might still be processing
          // Wait a bit and check again
          setTimeout(async () => {
            const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
            if (retryError) {
              console.error('Retry session error:', retryError);
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
              navigate('/login?error=verification_failed', { replace: true });
              return;
            }
            if (retrySession) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
              navigate('/', { replace: true });
            } else {
              // If still no session, redirect to login with error message
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
              navigate('/login?error=verification_failed', { replace: true });
            }
          }, 1500);
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        navigate('/login?error=verification_failed', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f8f9fa', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center' 
    }}>
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <h2>Verifying your email...</h2>
        <p>Please wait while we verify your email address.</p>
        <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '10px' }}>
          You'll be redirected automatically.
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
